import React, {Component} from 'react';
import {connect} from 'react-redux';
import {uiActions} from '../chrome';
import {Dialog, Dropdown, ValidatingInput} from '../widgets/';
import './ProfileEditDetails.css';
import actions from './editingProfile.actions';
import LocationSearchDropdown from '../search/LocationSearchDropdown';
import Icon from '../widgets/Icon';
import ChooseBackground from '../widgets/ChooseBackground';
import {FIXED_IMAGES, FIXED_IMAGES_UUIDS} from './fixedImages';

const PROVIDER_LABELS = {
	firstname: 'First name',
	lastname: 'Last name',
	businessName: 'Business name',
	brief: 'Brief',
	about: 'About you',
	backgroundImage: 'Background image',
	location: 'Location',
	category: 'Category',
	profession: 'Profession'
};

const COMMUNITY_LABELS = {
	...PROVIDER_LABELS,
	name: 'Community name',
	about: 'About this community'
};

const PROVIDER_PLACEHOLDERS = {
	firstname: 'What is your first name?',
	lastname: 'What is your last name?',
	businessName: 'What is the name of your business?',
	brief: 'Enter a short tagline that sells you best',
	about: 'About you',
	backgroundImage: 'Background image',
	location: 'Location',
	category: 'Category',
	profession:
		'List the services that you provide (e.g. Lettering | Wedding & Events | Hair & Makeup )'
};

const COMMUNITY_PLACEHOLDERS = {
	...PROVIDER_PLACEHOLDERS,
	name: 'What is the name of this community?',
	about:
		'Write a detailed explanation of the range of activities or services this community providers',
	brief: 'Enter a short description of this community'
};

/**
 * Check that the personal name fields (first/last) are not being used for business name.
 */
export const businessNameNotInPersonalName = (firstname, lastname, businessName) => {
	if ((!firstname && !lastname) || !businessName) return true;
	const personalName = `${firstname} ${lastname}`.trim().toLowerCase();
	const businessNameTrimmed = businessName.trim().toLowerCase();

	return (
		!personalName ||
		!businessNameTrimmed ||
		(personalName.indexOf(businessNameTrimmed) < 0 && businessNameTrimmed.indexOf(personalName) < 0)
	);
};

export const nameStates = {
	valid: {
		icon: 'icon-check',
		colour: 'green',
		title: '',
		message: ''
	},
	businessName: {
		icon: 'icon-warning',
		colour: '#ffc20e',
		title: 'Please use your personal name in the first and last name fields',
		message: 'Business in personal name'
	},
	empty: {
		icon: 'icon-warning',
		colour: '#ffc20e',
		title: 'Please add your personal name to your profile',
		message: 'Required'
	}
};

export class ProfileEditDetails extends Component {
	getContrastYIQ(hexcolor) {
		var r = parseInt(hexcolor.substr(0, 2), 16);
		var g = parseInt(hexcolor.substr(2, 2), 16);
		var b = parseInt(hexcolor.substr(4, 2), 16);
		var yiq = (r * 299 + g * 587 + b * 114) / 1000;
		return yiq >= 128 ? false : true;
	}

	render() {
		if (!this.props.editingProfile) return null;

		const {
			editingProfile: {
				type,
				profile: {brief, about, backgroundImage, customColour},
				profession,
				category,
				subscriptionLevel
			},
			suburbs,
			providerCategories,
			communityCategories,
			onSave,
			onCancel,
			onProviderFirstnameChange,
			onProviderLastnameChange,
			onProviderBusinessNameChange,
			onCommunityNameChange,
			onProfessionChange,
			onBriefChange,
			onAboutChange,
			onCategoryChange,
			onLocationChange,
			onBackgroundChange,
			onCustomColourChange,
			screenSize,
			subscription
		} = this.props;

		let canSave = false,
			name,
			firstname,
			lastname,
			title,
			location,
			labels,
			placeholders,
			onNameChange,
			categories,
			businessName,
			validCustomColour = true; // true by default because not required

		// Branch to find the subtle differences between profile types
		if (type === 'provider') {
			title = 'Edit profile details';
			firstname = this.props.editingProfile.user.firstname;
			lastname = this.props.editingProfile.user.lastname;
			businessName = this.props.editingProfile.user.businessName;
			location = this.props.editingProfile.user.location;
			labels = PROVIDER_LABELS;
			placeholders = PROVIDER_PLACEHOLDERS;
			categories = providerCategories;
			canSave = firstname && firstname.length && lastname && lastname.length && location;
		} else if (type === 'community') {
			title = this.props.editingProfile.isNew ? 'Create a community' : 'Edit community details';
			name = this.props.editingProfile.name;
			location = this.props.editingProfile.location;
			labels = COMMUNITY_LABELS;
			placeholders = COMMUNITY_PLACEHOLDERS;
			onNameChange = onCommunityNameChange;
			categories = communityCategories;
			if (customColour) {
				validCustomColour = this.getContrastYIQ(customColour.replace('#', ''));
			}
			canSave = name && name.length && location && validCustomColour;
		} else {
			return null; // Un supported profile type
		}

		return (
			<Dialog
				onSave={onSave}
				onCancel={onCancel}
				disabled={{save: !canSave}}
				title={title}
				submitOnEnter={false}>
				<ul className="ProfileEditDetails">
					{this.props.editingProfile.isNew && type === 'community' ? (
						<div className="ProfileEditDetails-warning">
							<p>
								<Icon name="warning" />
								Communities require approval from site administrators. We'll be in touch soon!
							</p>
						</div>
					) : (
						undefined
					)}
					{type === 'provider' && (
						<li>
							<label>
								<h3>{labels.firstname}</h3>
								<ValidatingInput
									maxLength={128}
									className="ProfileEditDetails-firstname"
									placeholder={placeholders.firstname}
									value={firstname}
									states={nameStates}
									onChange={onProviderFirstnameChange}
									state={
										firstname && firstname.length
											? businessNameNotInPersonalName(firstname, lastname, businessName)
												? 'valid'
												: 'businessName'
											: 'empty'
									}
									validIcon=""
								/>
							</label>
						</li>
					)}
					{type === 'provider' && (
						<li>
							<label>
								<h3>{labels.lastname}</h3>
								<ValidatingInput
									maxLength={128}
									className="ProfileEditDetails-lastname"
									placeholder={placeholders.lastname}
									value={lastname}
									onChange={onProviderLastnameChange}
									states={nameStates}
									state={
										lastname && lastname.length
											? businessNameNotInPersonalName(firstname, lastname, businessName)
												? 'valid'
												: 'businessName'
											: 'empty'
									}
									validIcon=""
								/>
							</label>
						</li>
					)}
					{type === 'provider' && subscriptionLevel !== 'none' && (
						<li>
							<label>
								<h3>{labels.businessName}</h3>
								<input
									type="text"
									maxLength={128}
									className="ProfileEditDetails-lastname"
									placeholder={placeholders.businessName}
									value={businessName}
									onChange={onProviderBusinessNameChange}
								/>
							</label>
						</li>
					)}
					{type !== 'provider' && (
						<li>
							<label>
								<h3>{labels.name}</h3>
								<ValidatingInput
									maxLength={128}
									className="ProfileEditDetails-name"
									placeholder={placeholders.name}
									value={name}
									onChange={onNameChange}
									isValid={!!(name && name.length)}
									invalidMessage="Required"
									invalidTitle="A name is required for a community's profile"
									validIcon=""
								/>
							</label>
						</li>
					)}
					{type === 'provider' && subscriptionLevel !== 'none' ? (
						<li>
							<label>
								<h3>{labels.profession}</h3>
								<input
									maxLength={64}
									className="ProfileEditDetails-profession"
									placeholder={placeholders.profession}
									value={profession}
									onChange={onProfessionChange}
								/>
							</label>
						</li>
					) : (
						undefined
					)}
					{type !== 'provider' || subscriptionLevel !== 'none' ? (
						<li>
							<label>
								<h3>{labels.category}</h3>
								<Dropdown
									scrollInOnShow={screenSize === 'mobile' || screenSize === 'tablet'}
									className="ProfileEditDetails-category"
									items={
										categories &&
										categories.map(category => ({
											value: category,
											label: category
										}))
									}
									value={category}
									onSelect={onCategoryChange}
								/>
							</label>
						</li>
					) : (
						undefined
					)}
					<li>
						<label>
							<h3 ref={el => (this.elLocation = el)}>{labels.location}</h3>
							<LocationSearchDropdown
								scrollInOnShow={screenSize === 'mobile' || screenSize === 'tablet'}
								className="ProfileEditDetails-location"
								value={location && location.suburb}
								extra={location && location.postcode}
								onSelect={onLocationChange}
								scrollIntoViewElement={() => this.elLocation}
							/>
						</label>
					</li>

					{type !== 'provider' || subscriptionLevel !== 'none' ? (
						<li>
							<label>
								<h3>{labels.brief}</h3>
								<textarea
									spellCheck={true}
									maxLength={140}
									className="ProfileEditDetails-brief"
									placeholder={placeholders.brief}
									value={brief}
									onChange={onBriefChange}
								/>
								<div className="ProfileEditDetails-brief-counter">
									{(brief || '').length} / 140 characters
								</div>
							</label>
						</li>
					) : (
						undefined
					)}
					{type !== 'provider' || subscriptionLevel !== 'none' ? (
						<li>
							<label>
								<h3>{labels.about}</h3>
								<textarea
									spellCheck={true}
									maxLength={10000}
									className="ProfileEditDetails-about"
									placeholder={placeholders.about}
									value={about}
									onChange={onAboutChange}
								/>
							</label>
						</li>
					) : (
						undefined
					)}
					{type !== 'provider' && subscription === true ? (
						<li>
							<label>
								<h3>Select a brand colour</h3>
								<div style={{display: 'flex', flexDirection: 'row', marginBottom: '8px'}}>
									<input
										style={{flex: 1, height: '40px'}}
										type="color"
										value={customColour}
										onChange={onCustomColourChange}
									/>
									<input
										style={{flex: 5, paddingLeft: '1em'}}
										className="ProfileEditDetails-customColour"
										maxLength={9}
										placeholder="Hex code"
										value={customColour}
										onChange={onCustomColourChange}
									/>
								</div>
								{!validCustomColour ? (
									<span style={{color: 'red'}}>This colour is too light! Please choose again.</span>
								) : null}
							</label>
						</li>
					) : (
						undefined
					)}
					<li>
						<label>
							<h3>{labels.backgroundImage}</h3>
							<ChooseBackground
								onSelect={onBackgroundChange}
								selected={
									backgroundImage &&
									FIXED_IMAGES_UUIDS.indexOf(backgroundImage) >= 0 &&
									FIXED_IMAGES[FIXED_IMAGES_UUIDS.indexOf(backgroundImage)]
								}
								items={FIXED_IMAGES}
							/>
						</label>
					</li>
				</ul>
			</Dialog>
		);
	}
}

const ConnectedProfileEditDetails = connect(
	({editingProfile, location, categories, ui, community}) => ({
		editingProfile,
		suburbs: location && location.suburbs,
		providerCategories: categories && categories.provider,
		communityCategories: categories && categories.community,
		screenSize: ui.screenSize,
		subscription: community && community.subscription
	}),
	dispatch => ({
		onBackgroundChange: url =>
			dispatch(
				actions.editingProfileUpdate({
					profile: {
						backgroundImage: FIXED_IMAGES_UUIDS[FIXED_IMAGES.indexOf(url)]
					}
				})
			),
		onProviderFirstnameChange: e =>
			dispatch(actions.editingProfileUpdate({user: {firstname: e.target.value}})),
		onProviderLastnameChange: e =>
			dispatch(actions.editingProfileUpdate({user: {lastname: e.target.value}})),
		onProviderBusinessNameChange: e =>
			dispatch(actions.editingProfileUpdate({user: {businessName: e.target.value}})),
		onCommunityNameChange: e => dispatch(actions.editingProfileUpdate({name: e.target.value})),
		onProfessionChange: e => dispatch(actions.editingProfileUpdate({profession: e.target.value})),
		onBriefChange: e => dispatch(actions.editingProfileUpdate({profile: {brief: e.target.value}})),
		onAboutChange: e => dispatch(actions.editingProfileUpdate({profile: {about: e.target.value}})),
		onCustomColourChange: e =>
			dispatch(actions.editingProfileUpdate({profile: {customColour: e.target.value}})),
		onCategoryChange: category => dispatch(actions.editingProfileUpdate({category})),
		onLocationChange: location => dispatch(actions.updateLocation(location)),
		onSave: () => {
			dispatch(actions.persist());
			dispatch(uiActions.uiModal(null));
		},
		onCancel: () => {
			dispatch(actions.editingProfileDone());
			dispatch(uiActions.uiModal(null));
		}
	})
)(ProfileEditDetails);

export default ConnectedProfileEditDetails;



// WEBPACK FOOTER //
// ./src/profile/ProfileEditDetails.js